import React, { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import patientStatusConfigService, { PatientStatusConfig } from '../services/patientStatusConfigService';

export const PatientStatusConfigPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [statuses, setStatuses] = useState<PatientStatusConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [canScheduleMeeting, setCanScheduleMeeting] = useState(true);

  const loadStatuses = React.useCallback(async () => {
    if (!currentTenant) return;
    try {
      setIsLoading(true);
      const data = await patientStatusConfigService.getAll(currentTenant.id);
      setStatuses(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load patient statuses:', err);
      setError('Failed to load patient statuses');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setKey('');
    setTitle('');
    setCanScheduleMeeting(true);
  };

  const handleEdit = (status: PatientStatusConfig) => {
    setEditingId(status.id);
    setIsAdding(false);
    setKey(status.key);
    setTitle(status.title);
    setCanScheduleMeeting(status.can_schedule_meeting);
  };

  const handleSave = async () => {
    if (!currentTenant || !key.trim() || !title.trim()) return;

    try {
      if (isAdding) {
        await patientStatusConfigService.create(currentTenant.id, { key, title, can_schedule_meeting: canScheduleMeeting });
      } else if (editingId) {
        await patientStatusConfigService.update(editingId, { key, title, can_schedule_meeting: canScheduleMeeting });
      }
      await loadStatuses();
      handleCancel();
    } catch (err) {
      console.error('Failed to save patient status:', err);
      setError('Failed to save patient status');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this patient status?')) return;

    try {
      await patientStatusConfigService.delete(id);
      await loadStatuses();
    } catch (err) {
      console.error('Failed to delete patient status:', err);
      setError('Failed to delete patient status');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setKey('');
    setTitle('');
    setCanScheduleMeeting(true);
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Patient Statuses</h1>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            + Add Patient Status
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">
              {isAdding ? 'Add New Patient Status' : 'Edit Patient Status'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key
                </label>
                <input
                  type="text"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter status key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter status title"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="canScheduleMeeting"
                checked={canScheduleMeeting}
                onChange={(e) => setCanScheduleMeeting(e.target.checked)}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor="canScheduleMeeting" className="text-sm font-medium text-gray-700">
                Can Schedule Meeting
              </label>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Patient Statuses Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Can Schedule Meeting
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {statuses.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No patient statuses found. Click "Add Patient Status" to create one.
                  </td>
                </tr>
              ) : (
                statuses.map((status) => (
                  <tr key={status.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {status.key}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {status.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {status.can_schedule_meeting ? 'Yes' : 'No'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(status)}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(status.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
